<script setup>
/* eslint-disable no-unused-vars */ // Las variables de <script setup> se usan en el template
import { computed, onMounted, ref } from "vue"; // eslint-disable-line
import { useRoute, useRouter } from "vue-router"; // eslint-disable-line
import api from "@/servicios/apiSRP"; // eslint-disable-line
import swal from "sweetalert";
import BotonPrimario from "../Areas/Requisicion Pago/components/BotonPrimario.vue"; // eslint-disable-line

const route = useRoute();
const router = useRouter();
const apiContrasena = api.SRP.RequisicionPago.RecuperacionContrasena;

/**
 * Dos pantallas en una:
 * - /recuperar-contrasena: pide el correo y envía el enlace.
 * - /definir-contrasena?colaborador=&token=: valida el enlace y guarda la nueva contraseña.
 */
const esDefinir = computed(() => route.name == "srp-definir-contrasena");
const colaboradorId = computed(() => Number(route.query.colaborador || 0));
const token = computed(() => (route.query.token || "").toString());

const email = ref((route.query.email || "").toString());
const solicitudEnviada = ref(false);
const mensajeSolicitud = ref("");

const password = ref("");
const confirmacion = ref("");
const mostrarPassword = ref(false);
const validandoEnlace = ref(false);
const enlaceValido = ref(false);
const guardada = ref(false);

const procesando = ref(false);
const formularioValidado = ref(false);

/** Mismas reglas que valida el servidor */
const reglas = computed(() => [
  { texto: "Al menos 8 caracteres", ok: password.value.length >= 8 },
  { texto: "Al menos una letra", ok: /[a-zA-ZÀ-ÿ]/.test(password.value) },
  { texto: "Al menos un número", ok: /\d/.test(password.value) },
  { texto: "Ambas contraseñas coinciden", ok: !!password.value && password.value == confirmacion.value }
]);
const passwordValida = computed(() => reglas.value.every((r) => r.ok));

function MensajeError(error) {
  const mensaje = error && error.response && error.response.data && error.response.data.mensaje;
  return mensaje || "No fue posible completar la operación. Intenta más tarde.";
}

async function Solicitar() {
  if (procesando.value) return;
  formularioValidado.value = true;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) return;
  procesando.value = true;
  try {
    const { data } = await apiContrasena.Solicitar({ email: email.value.trim() });
    mensajeSolicitud.value = data.mensaje;
    solicitudEnviada.value = true;
  } catch (error) {
    swal("¡Ups!", MensajeError(error), "error");
  } finally {
    procesando.value = false;
  }
}

async function ValidarEnlace() {
  if (!colaboradorId.value || !token.value) {
    enlaceValido.value = false;
    return;
  }
  validandoEnlace.value = true;
  try {
    const { data } = await apiContrasena.ValidarToken(colaboradorId.value, token.value);
    if (data.estado == "ERROR") swal("¡Ups!", data.mensaje, "error");
    enlaceValido.value = data.data === true;
  } catch (error) {
    enlaceValido.value = false;
  } finally {
    validandoEnlace.value = false;
  }
}

async function Definir() {
  if (procesando.value) return;
  formularioValidado.value = true;
  if (!passwordValida.value) return;
  procesando.value = true;
  try {
    const { data } = await apiContrasena.Definir({
      colaboradorId: colaboradorId.value,
      token: token.value,
      password: password.value,
      confirmacion: confirmacion.value
    });
    if (data.estado != "OK") {
      swal("Atención", data.mensaje, data.estado == "ERROR" ? "error" : "warning");
      return;
    }
    guardada.value = true;
    password.value = "";
    confirmacion.value = "";
  } catch (error) {
    swal("¡Ups!", MensajeError(error), "error");
  } finally {
    procesando.value = false;
  }
}

function IrALogin() {
  router.push({ name: "srp-login" });
}

onMounted(async () => {
  if (esDefinir.value) await ValidarEnlace();
});
</script>
<template>
  <div class="login">
    <!-- Solicitar enlace -->
    <form
      v-if="!esDefinir"
      class="form"
      novalidate
      :class="{ 'was-validated': formularioValidado && !solicitudEnviada }"
      @submit.prevent="Solicitar"
    >
      <h1 class="d-inline tituloHeader text-center">SRP</h1>
      <template v-if="!solicitudEnviada">
        <div>
          <h2 class="subtitulo">¿Olvidaste tu contraseña?</h2>
          <p class="texto">Escribe tu correo y te enviaremos un enlace para crear una nueva.</p>
        </div>
        <div class="form-floating">
          <input
            id="emailRecuperar"
            type="email"
            v-model="email"
            class="form-control"
            placeholder="usuario@hircasa.com.mx"
            required
          />
          <label for="emailRecuperar">Correo</label>
          <div class="invalid-feedback">Ingresa un correo válido.</div>
        </div>
        <div class="d-grid">
          <BotonPrimario
            :cargando="procesando"
            icono="fas fa-paper-plane"
            color="btn-primary text-white"
            texto="Enviar enlace"
            mensaje="Enviando"
            class="BotonPrimario"
          ></BotonPrimario>
        </div>
      </template>
      <template v-else>
        <div class="estado">
          <i class="fas fa-envelope-open-text"></i>
          <p class="texto mb-0">{{ mensajeSolicitud }}</p>
        </div>
      </template>
      <a href="#" class="volver" @click.prevent="IrALogin"><i class="fas fa-arrow-left"></i> Volver a iniciar sesión</a>
    </form>

    <!-- Definir contraseña -->
    <form
      v-else
      class="form"
      novalidate
      :class="{ 'was-validated': formularioValidado && !guardada }"
      @submit.prevent="Definir"
    >
      <h1 class="d-inline tituloHeader text-center">SRP</h1>

      <p v-if="validandoEnlace" class="texto text-center">Validando enlace...</p>

      <template v-else-if="guardada">
        <div class="estado ok">
          <i class="fas fa-check-circle"></i>
          <p class="texto mb-0">Tu contraseña se guardó correctamente. Ya puedes iniciar sesión.</p>
        </div>
        <div class="d-grid">
          <button class="btn btn-primary text-white" type="button" @click="IrALogin">Iniciar sesión</button>
        </div>
      </template>

      <template v-else-if="!enlaceValido">
        <div class="estado error">
          <i class="fas fa-link"></i>
          <p class="texto mb-0">
            Este enlace ya no es válido. Puede que ya se haya usado o que se haya generado uno más reciente.
          </p>
        </div>
        <router-link :to="{ name: 'srp-recuperar-contrasena' }" class="btn btn-outline-primary">
          Solicitar un nuevo enlace
        </router-link>
      </template>

      <template v-else>
        <div>
          <h2 class="subtitulo">Define tu contraseña</h2>
          <p class="texto">Es la contraseña de tu cuenta corporativa: también la usarás en las demás aplicaciones.</p>
        </div>
        <div class="form-floating">
          <input
            id="nuevaPassword"
            :type="mostrarPassword ? 'text' : 'password'"
            v-model="password"
            class="form-control"
            placeholder="Nueva contraseña"
            autocomplete="new-password"
            maxlength="64"
            required
          />
          <label for="nuevaPassword">Nueva contraseña</label>
        </div>
        <div class="form-floating">
          <input
            id="confirmarPassword"
            :type="mostrarPassword ? 'text' : 'password'"
            v-model="confirmacion"
            class="form-control"
            placeholder="Confirma la contraseña"
            autocomplete="new-password"
            maxlength="64"
            required
          />
          <label for="confirmarPassword">Confirma la contraseña</label>
        </div>
        <div class="form-check">
          <input id="mostrarPassword" class="form-check-input" type="checkbox" v-model="mostrarPassword" />
          <label class="form-check-label" for="mostrarPassword">Mostrar contraseña</label>
        </div>
        <ul class="reglas">
          <li v-for="r in reglas" :key="r.texto" :class="{ ok: r.ok }">
            <i :class="r.ok ? 'fas fa-check' : 'fas fa-circle'"></i> {{ r.texto }}
          </li>
        </ul>
        <div class="d-grid">
          <BotonPrimario
            :cargando="procesando"
            :disabled="!passwordValida"
            icono="fas fa-lock"
            color="btn-primary text-white"
            texto="Guardar contraseña"
            mensaje="Guardando"
            class="BotonPrimario"
          ></BotonPrimario>
        </div>
      </template>
      <a href="#" class="volver" @click.prevent="IrALogin"><i class="fas fa-arrow-left"></i> Volver a iniciar sesión</a>
    </form>
  </div>
</template>
<style lang="scss" scoped>
@import "src/sass/bootstrap-variables.scss";

/* Mismo fondo y tarjeta que la pantalla de inicio de sesión */
.login {
  padding: 2rem;
  background-image: url("../../../assets/bg_login.jpg");
  background-repeat: no-repeat;
  background-size: cover;
  height: 100vh;
  min-height: 100vh;
}

.form {
  margin: 3rem auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 18px;
  width: 24%;
  min-width: 350px;
  max-width: 100%;
  background: rgb(255, 255, 255);
  border-radius: 9px;
  padding: 40px;
  box-shadow: 0 4px 10px 4px rgba(0, 0, 0, 0.3);

  h1 {
    color: $secondary;
    font-weight: 700;
    font-family: "Poppins";
  }

  .BotonPrimario {
    padding: 10px 0px !important;
  }
}

.subtitulo {
  font-size: 18px;
  color: $secondary;
  font-weight: 600;
  margin-bottom: 4px;
}

.texto {
  font-size: 14px;
  color: #6f6f6f;
}

.estado {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 8px;
  background: #fff1d6;

  i {
    font-size: 20px;
    color: $primary;
    margin-top: 2px;
  }

  &.ok {
    background: #e3f4ea;

    i {
      color: $success;
    }
  }

  &.error {
    background: #fbe4e6;

    i {
      color: $danger;
    }
  }
}

.reglas {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 13px;
  color: #8b8aa6;

  li {
    display: flex;
    gap: 8px;
    align-items: center;

    i {
      font-size: 8px;
      width: 12px;
      text-align: center;
    }

    &.ok {
      color: $success;

      i {
        font-size: 12px;
      }
    }
  }
}

.volver {
  font-size: 13px;
  text-align: center;
  color: $secondary;
  text-decoration: none;
}
</style>
